import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowDown,
  ArrowUp,
  Layers,
  Loader2,
  Plus,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { STAGE_PILL } from "@/pages/CustomPipelinePage";

const COLORS = Object.keys(STAGE_PILL);

export default function PipelinesSettingsTab() {
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState([]); // [{id,name,stages,lead_count}]
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/pipelines");
      setDrafts(
        (data?.pipelines || []).map((p) => ({
          ...p,
          stages: (p.stages || [])
            .slice()
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
        })),
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const patchDraft = (id, patch) =>
    setDrafts((d) => d.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const setStages = (id, fn) =>
    setDrafts((d) => d.map((p) => (p.id === id ? { ...p, stages: fn(p.stages) } : p)));

  const create = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      await api.post("/admin/pipelines", { name });
      setNewName("");
      toast.success(`Section "${name}" created`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const save = async (p) => {
    if (!p.stages.length) {
      toast.error("Add at least one stage");
      return;
    }
    if (p.stages.some((s) => !s.label.trim())) {
      toast.error("Stage names can't be empty");
      return;
    }
    setSavingId(p.id);
    try {
      await api.put(`/admin/pipelines/${p.id}`, {
        name: p.name,
        stages: p.stages.map((s, i) => ({
          id: s.id,
          label: s.label.trim(),
          color: s.color || "slate",
          order: i,
        })),
      });
      toast.success("Section saved");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (p) => {
    try {
      await api.delete(`/admin/pipelines/${p.id}`);
      toast.success("Section deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-slate-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="pipelines-settings-tab">
      <p className="max-w-2xl text-xs text-slate-500">
        Create your own sections (custom pipelines) like Leads &amp; Counselling. Name a section,
        then add, rename, recolour, reorder or remove its stages. Each section gets its own nav item
        and board. Admin only.
      </p>

      {/* Create new */}
      <div className="flex items-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50/50 p-4">
        <Layers className="h-5 w-5 shrink-0 text-[#1877F2]" />
        <Input
          placeholder="New section name (e.g. Visa Pipeline)"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && create()}
          className="h-10"
          data-testid="pipeline-new-name"
        />
        <Button
          onClick={create}
          disabled={creating || !newName.trim()}
          className="shrink-0 bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
          data-testid="pipeline-create-btn"
        >
          {creating ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}
          Create section
        </Button>
      </div>

      {drafts.length === 0 && (
        <div className="py-8 text-center text-xs text-slate-400">
          No custom sections yet. Create one above.
        </div>
      )}

      {drafts.map((p) => (
        <div
          key={p.id}
          className="rounded-xl border border-slate-200 bg-white p-5"
          data-testid={`pipeline-card-${p.id}`}
        >
          <div className="mb-4 flex items-center gap-3">
            <Input
              value={p.name}
              onChange={(e) => patchDraft(p.id, { name: e.target.value })}
              className="h-9 max-w-xs font-semibold text-[#0A2540]"
              data-testid={`pipeline-name-${p.id}`}
            />
            <span className="text-[11px] text-slate-400">{p.lead_count ?? 0} leads</span>
            <div className="ml-auto flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => remove(p)}
                className="text-rose-600 hover:bg-rose-50"
                data-testid={`pipeline-delete-${p.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                onClick={() => save(p)}
                disabled={savingId === p.id}
                className="bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
                data-testid={`pipeline-save-${p.id}`}
              >
                {savingId === p.id ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
                Save
              </Button>
            </div>
          </div>

          <Label className="text-xs uppercase tracking-[0.16em] text-slate-500">Stages</Label>
          <div className="mt-2 space-y-2">
            {p.stages.map((s, i) => (
              <div key={s.id || i} className="flex items-center gap-2" data-testid={`pipeline-stage-${p.id}-${i}`}>
                <span className={`h-2.5 w-2.5 rounded-full ${STAGE_PILL[s.color]?.split(" ")[0] || "bg-slate-200"}`} />
                <Input
                  value={s.label}
                  onChange={(e) => setStages(p.id, (st) => st.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                  className="h-9 flex-1"
                  data-testid={`pipeline-stage-label-${p.id}-${i}`}
                />
                <Select
                  value={s.color}
                  onValueChange={(v) => setStages(p.id, (st) => st.map((x, j) => (j === i ? { ...x, color: v } : x)))}
                >
                  <SelectTrigger className="h-9 w-28" data-testid={`pipeline-stage-color-${p.id}-${i}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COLORS.map((c) => (
                      <SelectItem key={c} value={c}>
                        <span className="flex items-center gap-2">
                          <span className={`h-2.5 w-2.5 rounded-full ${STAGE_PILL[c].split(" ")[0]}`} />
                          {c}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  disabled={i === 0}
                  onClick={() => setStages(p.id, (st) => { const a = [...st]; [a[i - 1], a[i]] = [a[i], a[i - 1]]; return a; })}
                  className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 disabled:opacity-30"
                  data-testid={`pipeline-stage-up-${p.id}-${i}`}
                >
                  <ArrowUp className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  disabled={i === p.stages.length - 1}
                  onClick={() => setStages(p.id, (st) => { const a = [...st]; [a[i + 1], a[i]] = [a[i], a[i + 1]]; return a; })}
                  className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 disabled:opacity-30"
                  data-testid={`pipeline-stage-down-${p.id}-${i}`}
                >
                  <ArrowDown className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setStages(p.id, (st) => st.filter((_, j) => j !== i))}
                  className="rounded-md p-1.5 text-rose-400 hover:bg-rose-50"
                  data-testid={`pipeline-stage-remove-${p.id}-${i}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setStages(p.id, (st) => [...st, { id: undefined, label: "New stage", color: "slate" }])}
            className="mt-2 text-[#1877F2] hover:bg-blue-50"
            data-testid={`pipeline-add-stage-${p.id}`}
          >
            <Plus className="mr-1.5 h-4 w-4" /> Add stage
          </Button>
        </div>
      ))}
    </div>
  );
}
